import imageCompression from 'browser-image-compression';
import { useEffect, useRef, useState } from 'react';

import { intakeApi, type IntakeJob } from '../../services/intakeApi';
import { runIntake, type IntakeFailure } from '../../utils/runIntake';

const api = intakeApi('team');

const FAILURE: Record<IntakeFailure, string> = {
  quota: 'The site-wide daily limit for automatic fill-in is used up. Fill in the form below.',
  badUrl: 'Enter a link that starts with http or https.',
  badFile: 'Upload a jpg, png or webp image up to 10 MB.',
  fetch: 'That link could not be opened. Try a screenshot.',
  tooLittle: 'That page has too little text. Try a screenshot.',
  timeout: 'That took too long. Fill in the form below.',
  failed: 'Could not read it. Fill in the form below.',
};

/**
 * Admin path B of smart intake (PRD §6.1): a link or an image fills the new Pick form.
 * Shown only while PICK_INTAKE_ENABLED is on. Team drafts have only the site-wide cap.
 */
export function AdminPickIntake({
  onDraft,
  onUnresolved,
  onBusyChange,
  beforeStart,
}: {
  onDraft: (job: IntakeJob) => void;
  /** A timed-out job that wrote a draft, or whose abandon could not be confirmed. */
  onUnresolved?: (u: { draftPickId?: string | null; pendingJobId?: string }) => void;
  /** The form locks while a job runs (no typing that a late result could overwrite). */
  onBusyChange?: (busy: boolean) => void;
  /** Settles an earlier unresolved job; false stops a new job from starting. */
  beforeStart?: () => Promise<boolean>;
}) {
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<'url' | 'img'>('url');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const run = useRef<AbortController | null>(null);

  useEffect(() => {
    api
      .enabled()
      .then(setEnabled)
      .catch(() => setEnabled(false));
    return () => run.current?.abort();
  }, []);

  if (!enabled) return null;

  const start = async () => {
    setMessage(null);
    if (mode === 'url' ? !url.trim() : !file) {
      setMessage('Enter a link or choose an image.');
      return;
    }
    if (beforeStart && !(await beforeStart())) return;
    run.current?.abort();
    const ctrl = new AbortController();
    run.current = ctrl;
    setBusy(true);
    setSearching(false);
    onBusyChange?.(true);
    const out = await runIntake(mode === 'url' ? { url } : { file: file! }, ctrl.signal, {
      api,
      compress: (f) =>
        imageCompression(f, { maxSizeMB: 4, maxWidthOrHeight: 2560, useWebWorker: true }),
      onSearching: () => setSearching(true),
    });
    if (ctrl.signal.aborted) return;
    setBusy(false);
    onBusyChange?.(false);
    if (out.kind === 'done') onDraft(out.job);
    else if (out.kind === 'failed') {
      setMessage(FAILURE[out.reason]);
      if (out.draftPickId || out.pendingJobId) {
        onUnresolved?.({ draftPickId: out.draftPickId, pendingJobId: out.pendingJobId });
      }
    }
  };

  return (
    <div className="rounded-lg bg-gray-50 p-4">
      <div className="mb-2 text-sm font-medium text-gray-700">Fill in from a link or an image</div>
      <div className="mb-3 flex gap-4 text-sm">
        <label className="inline-flex items-center gap-2">
          <input type="radio" checked={mode === 'url'} onChange={() => setMode('url')} />
          Link
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="radio" checked={mode === 'img'} onChange={() => setMode('img')} />
          Screenshot or poster
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {mode === 'url' ? (
          <input
            type="url"
            placeholder="https://"
            className="min-w-[260px] flex-1 rounded-lg border border-gray-300 px-4 py-2"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        ) : (
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="text-sm"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => void start()}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? (searching ? 'Searching the web...' : 'Reading...') : 'Fill in'}
        </button>
      </div>
      {busy && searching && (
        <p className="mt-2 text-sm text-gray-600">
          The page would not open, so we are looking it up on the web. This takes about a minute.
        </p>
      )}
      {message && <p className="mt-2 text-sm text-red-700">{message}</p>}
    </div>
  );
}
